import type { AvatarConfig } from './avatarTypes';
import type { AvatarCanvasHandle } from './AvatarCanvas';

/**
 * Export the avatar as a PNG file download.
 */
export async function exportPNG(
  canvasHandle: AvatarCanvasHandle,
  name: string,
  scale = 4,
): Promise<void> {
  const blob = await canvasHandle.getImageBlob(scale);
  if (!blob) return;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name || 'avatar'}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Encode an AvatarConfig into a URL-safe base64 string for sharing.
 */
export function encodeShareLink(config: AvatarConfig): string {
  const json = JSON.stringify(config);
  return btoa(unescape(encodeURIComponent(json)));
}

/**
 * Decode a share link parameter back into an AvatarConfig.
 * Returns null if the string is invalid.
 */
export function decodeShareLink(param: string): AvatarConfig | null {
  try {
    const json = decodeURIComponent(escape(atob(param)));
    const parsed = JSON.parse(json);
    // Basic validation
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof parsed.entityType === 'string' &&
      typeof parsed.palette === 'object'
    ) {
      return parsed as AvatarConfig;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Copy a share URL to clipboard.
 */
export async function copyShareLink(config: AvatarConfig): Promise<void> {
  const encoded = encodeShareLink(config);
  const url = `${window.location.origin}/avatar?v=${encoded}`;
  await navigator.clipboard.writeText(url);
}
